import { describeRules, dollars, summarizeRules } from "@loot-os/api-contract/shopifyRuleBoxForm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { type CreateBoxBlocker, useSetBoxOnSale } from "./boxSale";
import { EditRuleBox } from "./EditRuleBox";
import { useNow } from "./hooks";
import { BOX_STATUS } from "./labels";
import { RuleBoxBuilder } from "./RuleBoxBuilder";
import { RuleBoxHistoryPage } from "./RuleBoxHistoryPage";
import { describeRefresh } from "./refreshTime";
import { SellInStoreSwitch } from "./SellInStoreSwitch";
import { SETUP_STEP_TITLES } from "./setupSteps";
import { TABLE, TD, TH } from "./tableStyles";
import { useTRPC } from "./trpc";

/** The two pages of a box the merchant opens from the list: its builder, or its history. */
type BoxView = "setup" | "history";

const DELETE_MODAL_ID = "delete-box-modal";

/** Fixed column widths, so a ticking countdown or a long rule line never resizes the columns. */
const FIXED_TABLE = { ...TABLE, tableLayout: "fixed" } as const;

/** Cut to one line with an ellipsis. */
const ONE_LINE = { overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" } as const;

/**
 * The environment's rule boxes, each with its rules, price, last and next daily refresh, its
 * Shopify switch and its actions (Refresh now, History, Edit, Delete); and the builder for a new
 * one, whose "Create a box" is disabled until Home's four setup steps are done (`createBlocker`),
 * with a line beside it naming what is left. A box's name or Edit opens the same builder on that
 * box; History opens its list of rebuilds as a page of its own.
 */
export function BoxesScreen({ createBlocker }: { createBlocker: CreateBoxBlocker | null }) {
  const canCreate = createBlocker === null;
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const boxes = useQuery(trpc.boxes.list.queryOptions());
  const now = useNow(30_000);
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState<{ id: string; view: BoxView } | null>(null);
  const openBox = (id: string, view: BoxView = "setup") => setOpened({ id, view });
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: trpc.boxes.list.queryKey() });
  const setEnabled = useSetBoxOnSale();
  const refresh = useMutation(
    trpc.boxes.refresh.mutationOptions({
      onSuccess: (preview) => {
        if (preview.ok) shopify.toast.show("Box refreshed from your store's products.");
        else shopify.toast.show(`Couldn't rebuild the box: ${preview.message}`, { isError: true });
        invalidate();
        void queryClient.invalidateQueries({ queryKey: trpc.boxes.history.pathKey() });
      },
    }),
  );

  const [deleting, setDeleting] = useState<{ id: string; name: string; listed: boolean } | null>(
    null,
  );
  const remove = useMutation(
    trpc.boxes.delete.mutationOptions({
      onSuccess: (result) => {
        shopify.toast.show(
          result.outcome === "deleted"
            ? "Box deleted."
            : "Box removed. It's kept for your order history because customers opened or bought it.",
        );
        invalidate();
      },
    }),
  );

  if (opened?.view === "history") {
    return <RuleBoxHistoryPage boxId={opened.id} onBack={() => setOpened(null)} />;
  }

  if (opened) {
    return (
      <EditRuleBox
        key={opened.id}
        boxId={opened.id}
        onDone={() => {
          setOpened(null);
          invalidate();
          void queryClient.invalidateQueries({ queryKey: trpc.boxes.history.pathKey() });
        }}
        onCancel={() => setOpened(null)}
      />
    );
  }

  if (creating) {
    return (
      <RuleBoxBuilder
        start={null}
        onDone={() => {
          setCreating(false);
          invalidate();
        }}
        onCancel={() => setCreating(false)}
      />
    );
  }

  const rows = boxes.data ?? [];
  return (
    <>
      <s-stack gap="base">
        <s-paragraph>
          A loot box is a product your customers buy without knowing which item is inside. Tell us
          which of your products can be inside (by tag, vendor, type and price) and the margin you
          want, and we'll price the box and set the odds. Every day we rebuild it from your store,
          so sold-out products drop out and new ones join. Switch on "Sell in store" to list it like
          any other product.
        </s-paragraph>
        <s-stack direction="inline">
          <s-button
            variant="primary"
            disabled={!canCreate || undefined}
            onClick={() => setCreating(true)}
          >
            Create a box
          </s-button>
        </s-stack>
        {createBlocker && createBlocker !== "pending" && (
          <CreateBlockedNote blocker={createBlocker} />
        )}
        {boxes.isLoading ? (
          <s-paragraph>Loading your boxes…</s-paragraph>
        ) : boxes.isError ? (
          <s-banner tone="critical">
            Your boxes could not be loaded. Try again in a moment.
          </s-banner>
        ) : rows.length === 0 ? (
          <s-paragraph color="subdued">
            {canCreate
              ? `You haven't made a box yet. Click "Create a box" to start.`
              : "You haven't made a box yet."}
          </s-paragraph>
        ) : (
          <table style={FIXED_TABLE}>
            <colgroup>
              <col />
              <col style={{ width: 110 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 150 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={TH}>Box</th>
                <th style={TH}>Price</th>
                <th style={TH}>Status</th>
                <th style={TH}>Daily refresh</th>
                <th style={TH}>Sell in store</th>
                <th style={TH}>
                  <s-text accessibilityVisibility="exclusive">Actions</s-text>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((box) => {
                const status = BOX_STATUS[box.status];
                const refreshing = refresh.isPending && refresh.variables?.boxId === box.id;
                const refreshTime = describeRefresh(box.rule, now);
                return (
                  <tr key={box.id}>
                    <td style={TD}>
                      <s-grid
                        gridTemplateColumns="auto minmax(0, 1fr)"
                        gap="base"
                        alignItems="center"
                      >
                        <s-thumbnail
                          src={box.imageUrl || undefined}
                          alt={box.imageUrl ? box.name : "No image"}
                          size="small"
                        />
                        <s-stack gap="small-300">
                          <div style={ONE_LINE} title={box.name}>
                            <s-link
                              accessibilityLabel={`Edit ${box.name}`}
                              onClick={() => openBox(box.id)}
                            >
                              {box.name}
                            </s-link>
                          </div>
                          {/* One line whatever the rules, so the row never grows with them;
                              the whole of them is the hover title. */}
                          <div style={ONE_LINE} title={describeRules(box.rule.config.rules)}>
                            <s-text color="subdued">{summarizeRules(box.rule.config.rules)}</s-text>
                          </div>
                        </s-stack>
                      </s-grid>
                    </td>
                    <td style={TD}>
                      <s-stack gap="small-300">
                        <s-text>{box.currentVersion > 0 ? dollars(box.priceCents) : "—"}</s-text>
                        {box.currentVersion > 0 && (
                          <s-text color="subdued">
                            {box.itemCount === 1 ? "1 product" : `${box.itemCount} products`}
                          </s-text>
                        )}
                      </s-stack>
                    </td>
                    <td style={TD}>
                      <s-stack gap="small-300">
                        <s-stack direction="inline" gap="small-300">
                          <s-badge tone={status.tone}>{status.label}</s-badge>
                          {box.rule.refreshStatus === "unavailable" && (
                            <s-badge tone="critical">Can't rebuild</s-badge>
                          )}
                        </s-stack>
                        {box.rule.refreshStatus === "unavailable" && box.rule.refusalMessage && (
                          <s-text color="subdued">{box.rule.refusalMessage}</s-text>
                        )}
                        {box.rule.refreshStatus === "unavailable" && box.currentVersion > 0 && (
                          <s-text color="subdued">
                            It shows sold out in your store until it can be rebuilt.
                          </s-text>
                        )}
                      </s-stack>
                    </td>
                    <td style={TD}>
                      <s-stack gap="small-300">
                        <s-text color="subdued">{refreshTime.last}</s-text>
                        <s-text color="subdued" fontVariantNumeric="tabular-nums">
                          {refreshTime.next}
                        </s-text>
                      </s-stack>
                    </td>
                    <td style={TD}>
                      <SellInStoreSwitch box={box} setOnSale={setEnabled} />
                    </td>
                    <td style={TD}>
                      <s-stack direction="inline" gap="small-300">
                        <s-button
                          variant="tertiary"
                          icon="refresh"
                          accessibilityLabel={`Refresh ${box.name} now`}
                          interestFor={`refresh-tip-${box.id}`}
                          disabled={refresh.isPending || undefined}
                          loading={refreshing || undefined}
                          onClick={() => refresh.mutate({ boxId: box.id })}
                        />
                        <s-tooltip id={`refresh-tip-${box.id}`}>Refresh now</s-tooltip>
                        <s-button
                          variant="tertiary"
                          icon="clock"
                          accessibilityLabel={`History of ${box.name}`}
                          interestFor={`history-tip-${box.id}`}
                          onClick={() => openBox(box.id, "history")}
                        />
                        <s-tooltip id={`history-tip-${box.id}`}>History</s-tooltip>
                        <s-button
                          variant="tertiary"
                          icon="edit"
                          accessibilityLabel={`Edit ${box.name}`}
                          interestFor={`edit-tip-${box.id}`}
                          onClick={() => openBox(box.id)}
                        />
                        <s-tooltip id={`edit-tip-${box.id}`}>Edit</s-tooltip>
                        {box.deletable && (
                          <>
                            <s-button
                              variant="tertiary"
                              tone="critical"
                              icon="delete"
                              accessibilityLabel={`Delete ${box.name}`}
                              interestFor={`delete-tip-${box.id}`}
                              commandFor={DELETE_MODAL_ID}
                              command="--show"
                              disabled={remove.isPending || undefined}
                              onClick={() =>
                                setDeleting({
                                  id: box.id,
                                  name: box.name,
                                  listed: Boolean(box.shopify),
                                })
                              }
                            />
                            <s-tooltip id={`delete-tip-${box.id}`}>Delete</s-tooltip>
                          </>
                        )}
                      </s-stack>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </s-stack>
      <s-modal id={DELETE_MODAL_ID} heading={deleting ? `Delete ${deleting.name}?` : "Delete box?"}>
        <s-stack gap="base">
          {deleting?.listed && (
            <s-paragraph>
              Its product will be deleted from your store. Customers who bought it but haven't
              opened it yet can no longer reveal it; they can still exchange it for store credit.
            </s-paragraph>
          )}
          <s-paragraph>
            If customers have opened or bought this box, it's kept for your order history but no
            longer shown here. This can't be undone.
          </s-paragraph>
        </s-stack>
        <s-button
          slot="primary-action"
          variant="primary"
          tone="critical"
          commandFor={DELETE_MODAL_ID}
          command="--hide"
          onClick={() => deleting && remove.mutate({ boxId: deleting.id })}
        >
          Delete box
        </s-button>
        <s-button slot="secondary-actions" commandFor={DELETE_MODAL_ID} command="--hide">
          Cancel
        </s-button>
      </s-modal>
    </>
  );
}

/**
 * Why "Create a box" is disabled, beside it: Boxes has no setup guide to say. The plan's own
 * banner sits above with its approval, so the plan's lines only point at it.
 */
function CreateBlockedNote({ blocker }: { blocker: Exclude<CreateBoxBlocker, "pending"> }) {
  if (blocker === "plan") {
    return <s-paragraph color="subdued">Approve the Loot Boxes plan to create a box.</s-paragraph>;
  }
  if (blocker === "frozen") {
    return (
      <s-paragraph color="subdued">You can't create a box while the plan is paused.</s-paragraph>
    );
  }
  if (blocker === "failed") {
    return (
      <s-paragraph color="subdued">
        Shopify didn't say whether your theme and Thank-you page are set up, so you can't create a
        box yet. Reload the app to check again.
      </s-paragraph>
    );
  }
  return (
    <s-paragraph color="subdued">
      Finish the setup guide on <s-link href="/">Home</s-link> to create a box. Next:{" "}
      {SETUP_STEP_TITLES[blocker]}.
    </s-paragraph>
  );
}
